import React, { useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { Check, DeleteOutline, ErrorOutline } from '@mui/icons-material';
import { Colors } from 'library';
import {
    Card,
    Checkbox,
    Chip,
    DashedAddButton,
    IconButton,
    Modal,
    ModalFooter,
    PillButton,
    Row,
    Stack,
    Text,
    TextButton,
    Tooltip,
} from 'library/ui';
import { useUserContext } from 'UserContext';
import { verifyBankAccountMandate } from 'api/PaymentInformationApi';
import { PaymentMethodBadge } from './PaymentMethodBadge';
import { AssignedAddress, PaymentMethod, isBankAccount, isCreditCard } from './types';

interface PaymentMethodRowProps {
    method: PaymentMethod;
    showSubsDefault: boolean;
    showRecruitDefault: boolean;
    recruitDefaultLabel?: string;
    setRecruitDefaultLabel?: string;
    showAssignment?: boolean;
    availableAddresses?: AssignedAddress[];
    onSetSubsDefault: () => void;
    onSetRecruitDefault: () => void;
    onAssign?: (addressIds: number[]) => void;
    onUnassign?: (addressId: number) => void;
    onMandateAccepted?: () => void;
    onDelete: () => void;
}

function formatFullAddress(address: AssignedAddress): string {
    const cityState = [address.city, address.state].filter(Boolean).join(' ');
    return [address.street, cityState, address.zip_code].filter(Boolean).join(', ');
}

function getCenterName(address: AssignedAddress): string {
    return address.description || formatFullAddress(address);
}

export function PaymentMethodRow({
    method,
    showSubsDefault,
    showRecruitDefault,
    recruitDefaultLabel = 'Recruit Default',
    setRecruitDefaultLabel = 'Set Recruit Default',
    showAssignment = false,
    availableAddresses = [],
    onSetSubsDefault,
    onSetRecruitDefault,
    onAssign,
    onUnassign,
    onMandateAccepted,
    onDelete,
}: PaymentMethodRowProps) {
    const isBank = isBankAccount(method);
    const isCard = isCreditCard(method);
    const stripe = useStripe();
    const { user } = useUserContext();
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedAddressIds, setSelectedAddressIds] = useState<number[]>([]);
    const [consenting, setConsenting] = useState(false);

    function onConsent() {
        setConsenting(true);
        verifyBankAccountMandate(stripe, method.id, user?.customer_id)
            .then(() => onMandateAccepted?.())
            .finally(() => setConsenting(false));
    }

    const primaryLabel = isBank
        ? `${method.bank_name || 'Bank account'} •••• ${method.last4 || '????'}`
        : `•••• •••• •••• ${method.last4 || '????'}`;

    const secondaryLabel = isCard
        ? `${method.cardholder_name ? `${method.cardholder_name} · ` : ''}Expires ${String(method.exp_month ?? '').padStart(2, '0')}/${method.exp_year ?? ''}`
        : method.cardholder_name ?? '';

    const unassignedAddresses = availableAddresses.filter(
        (address) => !method.assigned_addresses.some((assigned) => assigned.id === address.id),
    );

    function toggleSelected(addressId: number) {
        setSelectedAddressIds((prev) =>
            prev.includes(addressId) ? prev.filter((id) => id !== addressId) : [...prev, addressId],
        );
    }

    function openAssignModal() {
        setSelectedAddressIds([]);
        setShowAssignModal(true);
    }

    function submitAssign() {
        onAssign?.(selectedAddressIds);
        setShowAssignModal(false);
    }

    return (
        <>
            <Card padding="md">
                <Stack gap="sm">
                    <Row justify="between" align="center">
                        <Row gap="md" align="center">
                            <PaymentMethodBadge method={method} />
                            <Stack gap="none">
                                <Row gap="sm" align="center">
                                    <Text weight="semibold">{primaryLabel}</Text>
                                    {method.needs_mandate && (
                                        <Chip
                                            variant="filled"
                                            bgColor={Colors.errorBackground}
                                            textColor={Colors.error}
                                            size="small"
                                        >
                                            Consent needed
                                        </Chip>
                                    )}
                                </Row>
                                {secondaryLabel && (
                                    <Text size="sm" tone="secondary">
                                        {secondaryLabel}
                                    </Text>
                                )}
                            </Stack>
                        </Row>
                        <Row gap="md" align="center">
                            {showSubsDefault && method.is_default && <DefaultChip label="Default" />}
                            {showRecruitDefault && method.is_recruit_default && (
                                <DefaultChip label={recruitDefaultLabel} />
                            )}
                            {showSubsDefault && !method.is_default && (
                                <TextButton onClick={onSetSubsDefault} color={Colors.textSecondary}>
                                    Set Default
                                </TextButton>
                            )}
                            {showRecruitDefault && !method.is_recruit_default && isCard && (
                                <TextButton onClick={onSetRecruitDefault} color={Colors.textSecondary}>
                                    {setRecruitDefaultLabel}
                                </TextButton>
                            )}
                            <IconButton icon={DeleteOutline} onClick={onDelete} />
                        </Row>
                    </Row>
                    {showAssignment && (
                        <Row gap="sm" align="center" wrap>
                            {method.assigned_addresses.map((address) => (
                                <Tooltip
                                    key={address.id}
                                    heading={getCenterName(address)}
                                    body={formatFullAddress(address)}
                                >
                                    <Chip
                                        variant="outlined"
                                        bgColor={Colors.fill}
                                        textColor={Colors.primary}
                                        borderColor={Colors.border}
                                        removable
                                        onRemove={() => onUnassign?.(address.id)}
                                    >
                                        {getCenterName(address)}
                                    </Chip>
                                </Tooltip>
                            ))}
                            {unassignedAddresses.length > 0 && (
                                <DashedAddButton size="sm" onClick={openAssignModal}>
                                    Assign
                                </DashedAddButton>
                            )}
                        </Row>
                    )}
                    {method.needs_mandate && (
                        <Card tone="error" padding="sm">
                            <Row justify="between" align="center" gap="md">
                                <Row gap="sm" align="center">
                                    <ErrorOutline sx={{ color: Colors.error, fontSize: 20 }} />
                                    <Text size="sm">Re-authorize this account to keep payments running.</Text>
                                </Row>
                                <PillButton disabled={consenting} onClick={onConsent}>
                                    {consenting ? 'Submitting...' : 'Agree & Consent'}
                                </PillButton>
                            </Row>
                        </Card>
                    )}
                </Stack>
            </Card>
            {showAssignModal && (
                <Modal title="Assign to location" onClose={() => setShowAssignModal(false)}>
                    <Stack gap="md">
                        {unassignedAddresses.map((address) => (
                            <Row key={address.id} gap="md" align="center">
                                <Checkbox
                                    checked={selectedAddressIds.includes(address.id)}
                                    onChange={() => toggleSelected(address.id)}
                                />
                                <Stack gap="none" grow>
                                    <Text weight="semibold">{getCenterName(address)}</Text>
                                    <Text size="sm" tone="secondary">
                                        {formatFullAddress(address)}
                                    </Text>
                                </Stack>
                            </Row>
                        ))}
                    </Stack>
                    <ModalFooter>
                        <PillButton variant="secondary" onClick={() => setShowAssignModal(false)}>
                            Cancel
                        </PillButton>
                        <PillButton disabled={selectedAddressIds.length === 0} onClick={submitAssign}>
                            Assign
                        </PillButton>
                    </ModalFooter>
                </Modal>
            )}
        </>
    );
}

function DefaultChip({ label }: { label: string }) {
    return (
        <Chip variant="outlined" bgColor={Colors.fill} textColor={Colors.primary} borderColor={Colors.border}>
            <Check sx={{ fontSize: 14 }} />
            {label}
        </Chip>
    );
}
